import React, {FormEvent, memo, ReactNode, useCallback, useEffect, useRef, useState} from "react"
import {useDebounce} from "../../hooks/debounce"
import {RgbColor} from "./types"
import {Widget} from "../Widget"
import {onClickHandler} from "../../utils/onclick"
import {RgbColorPicker} from "./components/RgbColorPicker"

type Props = {
    color: RgbColor;
    name: string;
    value: number;
    onChange: (value: number) => void;
    onChangeColor: (value: RgbColor) => void;
    activeImage: string;
    passiveImage: string;
    height: string;
    width?: string;
    children?: ReactNode;
};

export const ColorPicker = memo(function RangeComponent(props: Props) {
    const {name, value: valueProp, onChange: onChangeProp, children, activeImage, passiveImage} = props

    const [tempValue, setTempValue] = useState(0)
    const [value, setValue] = useState(valueProp)
    const timeoutRef = useRef<number>()

    const onChangeCallback = useDebounce(onChangeProp, 600)

    const click = useCallback(() => {
        let newValue
        if (value > 0) {
            setTempValue(value)
            newValue = 0
        } else if (tempValue > 0) {
            newValue = tempValue
        } else {
            newValue = 100
        }

        setValue(newValue)
        onChangeCallback(newValue)
    }, [tempValue, value, onChangeCallback])

    const onChange = useCallback((e: FormEvent<HTMLInputElement>) => {
        // @ts-ignore
        const newValue = e.target.valueAsNumber
        if (newValue !== value) {
            setValue(newValue)
            onChangeCallback(newValue)
        }
    }, [onChangeCallback, value])

    useEffect(() => {
        setValue(valueProp)
    }, [valueProp])

    useEffect(() => {
        timeoutRef.current = setTimeout(() => {
            if (value !== valueProp) {
                setValue(valueProp)
            }
        }, 2000)

        return () => clearTimeout(timeoutRef.current)
    }, [value, valueProp])

    return (
        <Widget>
            <div className="position-relative">
                <div className="color-component-wrapper">
                    <div className="range">
                        <div className="range-image-wrapper">
                            <div className="range-image">
                                <div {...onClickHandler(click)}>
                                    <img
                                        src={value === 0 ? passiveImage : activeImage}
                                        alt={name} />
                                </div>
                            </div>
                        </div>
                        <div className="top-range-wrapper">
                            <div className="range-title">
                                <span>{name}</span>
                            </div>
                            <div className="range-control-value">
                        <span>
                            <span>{value}%</span>
                        </span>
                            </div>
                            <div className="range-slide-control">
                                <input type="range" name="points" value={value} min={0} max={100} step={10}
                                       onChange={onChange}
                                       onInput={onChange} />
                            </div>
                        </div>
                    </div>
                    <div className="color-picker-wrapper">
                        <RgbColorPicker
                            color={props.color}
                            height={props.height}
                            onChange={props.onChangeColor}
                        />
                    </div>
                </div>
                {children}
            </div>
        </Widget>
    )
})
